/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2014, 2019
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'./SlideTransitionController',
	'jquery',
	'underscore',
	'../../util/AnimationHelper'
], function(BaseClass, $, _, AnimationHelper) {

	var SlideShowTransitionController = BaseClass.extend({

		$fromShim: null,
		$toShim: null,
		$backdrop: null,

		_minScale: null,

		init: function(options) {
			SlideShowTransitionController.inherited('init', this, arguments);
			this._minScale = options.minScale ? options.minScale : 0.7;
		},

		_beforeNext: function(from, to) {
			// z-index manipulations are necessary for navigation across the end-of-story boundary
			this.$fromShim = this._makeShim(from.$el)
				.css(this._cssOnScreen)
				.css({ 'z-index': 1 });

			from.$el.css(this._cssOnScreen)
				.css({ 'z-index': 1 });

			this.$toShim = this._makeShim(to.$el)
				.css(this._cssOnScreen)
				.css({
					left: from.$el.width(),
					'z-index': 2,
					opacity: 1
				});

			to.$el
				.css(this._cssOnScreen)
				.css({
					left: from.$el.width(),
					'z-index': 2,
					opacity: 1
				});

			this._showWidgets(to);

			this.$backdrop = $('<div class="animationBackdrop"/>').insertBefore(this.$fromShim);
			return SlideShowTransitionController.inherited('_beforeNext', this, arguments);
		},

		_next: function(from, to) {
			return Promise.all([this._scaleOut(from), this._slideIn(to)]);
		},

		_afterNext: function(from, to) {
			this._hideWidgets(from);
			from.$el.css({ transform: AnimationHelper.SCALE_ZERO });
			this._cleanAnimationArtifacts(from, to);
			return SlideShowTransitionController.inherited('_afterNext', this, arguments);
		},

		_beforePrevious: function(from, to) {
			// z-index manipulations are necessary for navigation across the end-of-story boundary
			this.$fromShim = this._makeShim(from.$el)
				.css(this._cssOnScreen)
				.css({ 'z-index': 1 });
			from.$el.css(this._cssOnScreen)
				.css({ 'z-index': 1 });

			this.$toShim = this._makeShim(to.$el)
				.css(this._cssOnScreen)
				.css({
					'z-index': 2,
					opacity: 0
				});

			to.$el
				.css(this._cssOnScreen)
				.css({
					'z-index': 2,
					opacity: 0
				});

			this._showWidgets(to);

			this.$backdrop = $('<div class="animationBackdrop"/>').insertBefore(this.$toShim);
			return SlideShowTransitionController.inherited('_beforePrevious', this, arguments);
		},

		_previous: function(from, to) {
			return Promise.all([this._slideOut(from), this._scaleIn(to)]);
		},

		_afterPrevious: function(from) {
			this._hideWidgets(from);
			from.$el.css({
				transform: AnimationHelper.SCALE_ZERO
			});
			this._cleanAnimationArtifacts();
			return SlideShowTransitionController.inherited('_afterPrevious', this, arguments);
		},

		// Animations

		_slideIn: function(scene) {
			return new Promise((resolve) => {
				scene.$el.addClass('slideShowSlide');
				this._slideTo(this.$toShim, '0px', () => { });
				this._slideTo(scene.$el, '0px', () => {
					scene.$el.removeClass('slideShowSlide');
					resolve();
				});
			});
		},

		_slideOut: function(scene) {
			return new Promise((resolve) => {
				scene.$el.addClass('slideShowSlide');
				this._slideTo(this.$fromShim, scene.$el.width(), () => { });
				this._slideTo(scene.$el, scene.$el.width(), () => {
					scene.$el.removeClass('slideShowSlide');
					resolve();
				});
			});
		},

		_slideTo: function($el, left, complete) {
			$el.animate({
				left: left
			}, {
				duration: this._duration,
				complete: complete,
				queue: false,
				easing: 'swing'
			});
		},

		_scaleIn: function(scene) {
			return new Promise((resolve) => {
				scene.$el.addClass('slideShowScale');
				this._scaleInAnimation(this.$toShim, () => { });
				this._scaleInAnimation(scene.$el, () => {
					scene.$el.removeClass('slideShowScale');
					resolve();
				});
			});
		},

		_scaleInAnimation: function($el, complete) {
			var minScale = this._minScale;
			$el.animate({
				transform: 1,
				opacity: 1
			}, {
				step: function(now, fx) {
					if(fx.prop === 'transform') {
						$el.css({
							transform: 'scale(' + ((now * (1 - minScale)) + minScale) + ')'
						});
					}
				},
				duration: this._duration,
				complete: complete
			}, 'swing');
		},

		_scaleOut: function(scene) {
			return new Promise((resolve) => {
				scene.$el.addClass('slideShowScale');
				this._scaleOutAnimation(this.$fromShim, () => { });
				this._scaleOutAnimation(scene.$el, () => {
					scene.$el.removeClass('slideShowScale');
					scene.$el.css({ opacity: 1 });
					resolve();
				});
			});
		},

		_scaleOutAnimation: function($el, complete) {
			var minScale = this._minScale;
			var duration = this._duration * 0.8;

			$el.delay(this._duration - duration).animate({
				transform: 1,
				opacity: 0
			}, {
				step: function(now) {
					$el.css({
						transform: 'scale(' + ((now * (1 - minScale)) + minScale) + ')'
					});
				},
				duration: duration,
				complete: complete
			}, 'swing');
		},

		_makeShim: function($el) {
			var $shimRoot = $('<div class="animationShim"/>');
			var parents = $el.parents('.contentViewPane *').get();
			var $shimTree = $shimRoot;
			_.each(parents.reverse(), function(parent) {
				var $clone = $(parent.cloneNode(false));
				$shimTree.append($clone);
				$shimTree = $clone;
			});
			$shimRoot.insertBefore($el);
			return $shimRoot;
		},

		_cleanAnimationArtifacts: function(from, to) {
			if(from && from.$el) {
				from.$el.css({ 'z-index': '' });
			}

			if(to && to.$el) {
				to.$el.css({ 'z-index': '' });
			}

			if(this.$toShim) {
				this.$toShim.remove();
				this.$toShim = null;
			}

			if(this.$fromShim) {
				this.$fromShim.remove();
				this.$fromShim = null;
			}

			if(this.$backdrop) {
				this.$backdrop.remove();
				this.$backdrop = null;
			}
		}

	});
	return SlideShowTransitionController;
});
